/**
 * lib/energy/card-running-cost.ts — the ONE rule for a running-cost estimate
 * on a product card (FRIDGE-802, owner decision 09/07/2026: supported
 * estimates from verified model specs and current rate inputs, labelled as
 * estimates; missing facts stay unknown, never guessed).
 *
 * A card has no ZIP, so it cannot use the local rate the product page uses.
 * Instead of a hard-coded national figure of unknown vintage, the card prices
 * the model's certified annual kWh at the MEDIAN of the 51 state residential
 * rates in the committed EIA Form 861M dataset (lib/energy/data/
 * eia-current-residential.json — source, period and checksum recorded in the
 * file). The basis line names the rate and its period so the figure is never
 * read as "your bill"; the product page remains the place for the local rate.
 *
 * PURE and client-safe: the card formats, the loader maps, both from here.
 */
import current from './data/eia-current-residential.json'

export interface RunningCostBasis {
  /** Median of the state residential rates, cents per kWh. */
  centsPerKwh: number
  /** Source period of the dataset, e.g. "2026-06". */
  sourcePeriod: string
  /** Human label of that period, e.g. "June 2026". */
  periodLabel: string
  /** How many state rates the median is taken over. */
  states: number
}

function positive(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) && n > 0 && n <= 100 ? n : null
}

/** "2026-06" → "June 2026" (UTC, so the month cannot shift by timezone). */
function periodLabel(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period)
  if (!m) return period
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

let cached: RunningCostBasis | null | undefined

/** The rate basis every card uses; null if the dataset is unusable (then no card shows a cost). */
export function runningCostBasis(): RunningCostBasis | null {
  if (cached !== undefined) return cached
  const rates = (current.records ?? [])
    .filter((r) => typeof r.state_code === 'string' && /^[A-Z]{2}$/.test(r.state_code))
    .map((r) => positive(r.rate_cents_kwh))
    .filter((r): r is number => r !== null)
    .sort((a, b) => a - b)
  if (rates.length < 40 || typeof current.source_period !== 'string') {
    cached = null
    return cached
  }
  const mid = Math.floor(rates.length / 2)
  const median = rates.length % 2 ? rates[mid] : (rates[mid - 1] + rates[mid]) / 2
  cached = { centsPerKwh: Math.round(median * 100) / 100, sourcePeriod: current.source_period, periodLabel: periodLabel(current.source_period), states: rates.length }
  return cached
}

/** Whole dollars per year for a certified annual kWh figure, or null when either input is unsupported. */
export function annualRunningCostDollars(kwhPerYear: number | string | null | undefined, basis: RunningCostBasis | null = runningCostBasis()): number | null {
  const kwh = typeof kwhPerYear === 'number' ? kwhPerYear : Number(kwhPerYear)
  if (!basis || !Number.isFinite(kwh) || kwh <= 0 || kwh > 5000) return null
  return Math.round((kwh * basis.centsPerKwh) / 100)
}

/** "Energy ≈ $130/yr" — the card's line; empty when there is nothing supported to say.
 *  The word "Energy" is part of the line (owner 09/16/2026: "make more clear. is this savings?"). */
export function formatRunningCost(kwhPerYear: number | string | null | undefined): string {
  const dollars = annualRunningCostDollars(kwhPerYear)
  return dollars === null ? '' : `Energy ≈ $${dollars}/yr`
}

/** The tooltip that says what the estimate is: the kWh, the rate and its period. */
export function runningCostBasisLine(kwhPerYear: number | string | null | undefined): string {
  const basis = runningCostBasis()
  const dollars = annualRunningCostDollars(kwhPerYear, basis)
  if (!basis || dollars === null) return ''
  const kwh = Math.round(Number(kwhPerYear))
  return `Estimate: ${kwh} kWh/yr × ${basis.centsPerKwh.toFixed(1)}¢/kWh, median state residential rate (EIA Form 861M, ${basis.periodLabel}); the product page prices your ZIP.`
}
